import React, { useContext, useEffect, useState } from 'react';
import { Carousel } from 'react-bootstrap';
import { Link } from '@tanstack/react-router';
import Breadcrumbs from '../BreadCrumbs/BreadCrumbs';
import { DestinationContext } from '../../contexts/DestinationContext';
import ArticleBody from './ArticleBody';
import Faq from './Faq';
import styles from './UsefulInfoListing.module.scss';

const UsefulInfoListing = ({ articles = [], faqs = [] }) => {
  const destination = useContext(DestinationContext);
  const heroImages = (destination?.images?.info || []).slice(0, 3);
  const isMultipleHeroImages = heroImages.length > 1;
  const [activeSlug, setActiveSlug] = useState(articles[0]?.slug);

  useEffect(() => {
    if (articles.length === 0 && faqs.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length > 0) {
          setActiveSlug(visible[0].target.id);
        }
      },
      { rootMargin: '-15% 0px -70% 0px' }
    );

    articles.forEach((article) => {
      const el = document.getElementById(article.slug);
      if (el) observer.observe(el);
    });

    // The FAQ section is a sibling "page" the side nav links to as well —
    // track it the same way so its link highlights when scrolled into view.
    if (faqs.length > 0) {
      const faqEl = document.getElementById('faqs');
      if (faqEl) observer.observe(faqEl);
    }

    return () => observer.disconnect();
  }, [articles, faqs]);

  const breadCrumbData = [
    { title: 'Home', url: '/' },
    { title: 'Useful Information', url: 'useful-info' },
  ];

  return (
    <div className={styles.page}>
      {/* Title/description/OG tags are set server-side via the route's
          `head()` (see routes/useful-info/index.jsx). */}

      <header className={styles.hero}>
        {heroImages.length > 0 && (
          <div className={styles.heroCarousel}>
            <Carousel
              controls={isMultipleHeroImages}
              indicators={isMultipleHeroImages}
              interval={isMultipleHeroImages ? 5000 : null}
              pause={false}
            >
              {heroImages.map((image) => (
                <Carousel.Item key={image.url} style={{ height: '100%' }}>
                  <img
                    className={styles.heroImage}
                    src={image.url}
                    alt={image.title || destination?.name}
                  />
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        )}
        <div className={styles.heroScrim} />
        <div className={styles.heroIn}>
          <div className={styles.eyebrow}>
            {destination?.heroText?.info?.eyebrow || `Field Notes · ${destination?.name || ''}`}
          </div>
          {destination?.heroText?.info?.title || destination?.maraGuide?.title ? (
            <h1
              className={styles.title}
              dangerouslySetInnerHTML={{
                __html: destination?.heroText?.info?.title || destination?.maraGuide?.title || '',
              }}
            />
          ) : (
            <h1 className={styles.title}>
              A Rough Guide to <em>{destination?.name}</em>
            </h1>
          )}
          <p className={styles.lede}>
            {destination?.heroText?.info?.subtext || "Everything we've learned about visiting the ecosystem — fees, seasons, logistics and etiquette — kept as current as we can."}
          </p>
        </div>
      </header>

      <Breadcrumbs crumbs={breadCrumbData} />

      <div className={styles.content}>
        {articles.length > 0 ? (
          <div className={styles.layout}>
            <nav className={styles.sideNav} aria-label="Guide sections">
              <div className={styles.sideNavTitle}>On This Page</div>
              <ul className={styles.sideNavList}>
                {articles.map((article, index) => (
                  <li key={article._id || article.slug}>
                    <a
                      href={`#${article.slug}`}
                      className={`${styles.sideNavLink} ${activeSlug === article.slug ? styles.sideNavLinkActive : ''}`}
                    >
                      <span className={styles.sideNavNum}>{String(index + 1).padStart(2, '0')}</span>
                      {article.eyebrow || article.title}
                    </a>
                  </li>
                ))}
                {faqs.length > 0 && (
                  <li>
                    <a
                      href="#faqs"
                      className={`${styles.sideNavLink} ${activeSlug === 'faqs' ? styles.sideNavLinkActive : ''}`}
                    >
                      <span className={styles.sideNavNum}>FAQ</span>
                      Frequently Asked Questions
                    </a>
                  </li>
                )}
              </ul>
            </nav>

            <div className={styles.articles}>
              {articles.map((article, index) => (
                <section key={article._id || article.slug} id={article.slug} className={styles.section}>
                  <div className={styles.sectionNum}>
                    {String(index + 1).padStart(2, '0')} · Useful Information
                  </div>
                  <ArticleBody article={article} destinationName={destination?.name} headingLevel="h2" />
                  <Link
                    to="/useful-info/$slug"
                    params={{ slug: article.slug }}
                    className={styles.standaloneLink}
                  >
                    View as a standalone page →
                  </Link>
                </section>
              ))}
              <Faq faqs={faqs} destinationName={destination?.name} />
            </div>
          </div>
        ) : (
          <>
            <p className={styles.empty}>
              Useful information hasn't been added yet for {destination?.name}.
            </p>
            <Faq faqs={faqs} destinationName={destination?.name} />
          </>
        )}
      </div>
    </div>
  );
};

export default UsefulInfoListing;
